import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Star, Heart, ShoppingCart, Zap, Trophy } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useCurrency } from './CurrencySelector';
import { FlashSaleCountdown, isFlashActive, isFlashScheduled } from './FlashSaleCountdown';
import { usePageT } from '../i18n/usePageT';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

export const ProductCard = ({ product, onWishlistToggle, isWishlisted = false }) => {
  const { getLocalizedName, t } = useLanguage();
  const { format } = useCurrency();
  const { isAuthenticated } = useAuth();
  const { addToCart } = useCart();

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      toast.error(t('login') + ' ' + t('addToCart').toLowerCase());
      return;
    }
    
    try {
      await addToCart(product.id);
      toast.success(t('addToCart') + ' ✓');
    } catch (error) {
      toast.error(error.message || 'Error');
    }
  };

  const handleWishlistToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      toast.error(t('login'));
      return;
    }
    
    onWishlistToggle?.(product.id);
  };

  const T = usePageT();
  const [flashExpired, setFlashExpired] = useState(false);
  const [, forceTick] = useState(0);
  const flashActive = !flashExpired && isFlashActive(product);
  const flashScheduled = !flashActive && isFlashScheduled(product);

  const discount = flashActive
    ? Math.round((1 - product.flash_sale_price / product.price) * 100)
    : product.compare_price
      ? Math.round((1 - product.price / product.compare_price) * 100)
      : 0;

  return (
    <Link 
      to={`/product/${product.id}`} 
      className="card-product group block"
      data-testid={`product-card-${product.id}`}
    >
      {/* Image Container */}
      <div className="relative aspect-square overflow-hidden bg-gray-100">
        <img
          src={product.images?.[0] || 'https://via.placeholder.com/400'}
          alt={getLocalizedName(product)}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        
        {/* Badges */}
        <div className="absolute top-3 left-3 z-[1] flex flex-col items-start gap-1.5">
          {flashActive ? (
            <Badge className="bg-red-600 text-white font-bold hover:bg-red-600" data-testid={`flash-sale-badge-${product.id}`}>
              <Zap className="w-3 h-3 mr-1 fill-current" />
              -{discount}%
            </Badge>
          ) : discount > 0 && (
            <Badge className="bg-[#E9C46A] text-[#0F4C5C] font-bold">
              -{discount}%
            </Badge>
          )}
          {flashScheduled && (
            <Badge className="bg-amber-500 text-white font-bold hover:bg-amber-500" data-testid={`flash-upcoming-badge-${product.id}`}>
              <Zap className="w-3 h-3 mr-1" />
              {T('Bientôt : vente flash', 'Flash sale soon')}
            </Badge>
          )}
          {product.is_bestseller && (
            <Badge className="bg-[#0F4C5C] text-[#E9C46A] font-bold hover:bg-[#0F4C5C]" data-testid={`bestseller-badge-${product.id}`}>
              <Trophy className="w-3 h-3 mr-1" />
              {T('Meilleure vente', 'Best seller')}
            </Badge>
          )}
        </div>
        
        {/* Quick Actions */}
        <div className="absolute top-3 right-3 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            size="icon"
            variant="secondary"
            className={`w-9 h-9 rounded-full bg-white shadow-lg hover:bg-gray-50 ${
              isWishlisted ? 'text-red-500' : 'text-gray-600'
            }`}
            onClick={handleWishlistToggle}
            data-testid={`wishlist-btn-${product.id}`}
          >
            <Heart className={`w-4 h-4 ${isWishlisted ? 'fill-current' : ''}`} />
          </Button>
        </div>

        {/* Add to Cart Button */}
        <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            className="w-full bg-white text-[#0F4C5C] hover:bg-[#E9C46A] font-semibold"
            onClick={handleAddToCart}
            disabled={product.stock === 0}
            data-testid={`add-to-cart-btn-${product.id}`}
          >
            <ShoppingCart className="w-4 h-4 mr-2" />
            {product.stock === 0 ? t('outOfStock') : t('addToCart')}
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Vendor */}
        <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
          <span>{t('soldBy')}</span>
          <span className="text-[#2A9D8F] font-medium">{product.vendor_name}</span>
        </p>

        {/* Name */}
        <h3 className="font-semibold text-foreground mb-2 line-clamp-2 group-hover:text-[#0F4C5C] transition-colors">
          {getLocalizedName(product)}
        </h3>

        {/* Rating */}
        <div className="flex items-center gap-2 mb-3">
          <div className="flex items-center gap-1">
            <Star className="w-4 h-4 fill-[#E9C46A] text-[#E9C46A]" />
            <span className="text-sm font-medium">{product.rating?.toFixed(1) || '0.0'}</span>
          </div>
          <span className="text-xs text-muted-foreground">
            ({product.review_count || 0} {t('reviews').toLowerCase()})
          </span>
        </div>

        {/* Price */}
        <div className="flex items-center gap-2">
          <span className={`text-lg font-bold ${flashActive ? 'text-red-600' : 'text-[#0F4C5C]'}`}>
            {format(flashActive ? product.flash_sale_price : product.price)}
          </span>
          {flashActive ? (
            <span className="text-sm text-muted-foreground line-through">
              {format(product.price)}
            </span>
          ) : product.compare_price && (
            <span className="text-sm text-muted-foreground line-through">
              {format(product.compare_price)}
            </span>
          )}
        </div>

        {/* Upcoming Flash Sale Countdown */}
        {flashScheduled && (
          <div className="mt-2">
            <FlashSaleCountdown
              endDate={product.flash_sale_start}
              size="sm"
              upcoming
              onExpire={() => forceTick((n) => n + 1)}
              testId={`flash-upcoming-countdown-${product.id}`}
            />
          </div>
        )}

        {/* Flash Sale Countdown */}
        {flashActive && (
          <div className="mt-2">
            <FlashSaleCountdown
              endDate={product.flash_sale_end}
              size="sm"
              onExpire={() => setFlashExpired(true)}
              testId={`flash-countdown-${product.id}`}
            />
          </div>
        )}

        {/* Stock Status */}
        {product.stock <= 5 && product.stock > 0 && (
          <p className="text-xs text-orange-600 mt-2">
            {product.stock} {t('inStock').toLowerCase()}
          </p>
        )}
      </div>
    </Link>
  );
};
